import { useEffect, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Pencil, Trash2, Play } from 'lucide-react'
import { toast } from 'sonner'

import { videoTestimonials as vtApi } from '../api/endpoints'
import type { AdminVideoTestimonial } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { Dialog, DialogClose, DialogContent } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input } from '../components/Input'
import { PageLoader } from '../components/Spinner'

export function VideoTestimonialsPage() {
  const [data, setData] = useState<AdminVideoTestimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [productId, setProductId] = useState('')
  const [author, setAuthor] = useState('')
  const [location, setLocation] = useState('')
  const [thumbnailUrl, setThumbnailUrl] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [duration, setDuration] = useState('')

  function reload() {
    setLoading(true)
    vtApi.list().then((r) => setData(r.videos))
      .catch((err) => toast.error('Failed to load', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  function resetForm() { setProductId(''); setAuthor(''); setLocation(''); setThumbnailUrl(''); setVideoUrl(''); setDuration('') }

  function openCreate() { resetForm(); setCreating(true) }

  function openEdit(v: AdminVideoTestimonial) {
    setProductId(v.productId); setAuthor(v.author); setLocation(v.location || '')
    setThumbnailUrl(v.thumbnail); setVideoUrl(v.videoUrl); setDuration(v.duration)
    setEditing(v.id)
  }

  async function handleSave() {
    setSaving(true)
    try {
      const payload = { productId, author, location, thumbnailUrl, videoUrl, duration }
      if (editing) {
        await vtApi.update(editing, payload)
        toast.success('Updated')
      } else {
        await vtApi.create(payload)
        toast.success('Created')
      }
      setCreating(false); setEditing(null); resetForm(); reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally { setSaving(false) }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete?')) return
    try { await vtApi.delete(id); toast.success('Deleted'); reload() }
    catch (err) { toast.error('Failed', { description: (err as Error).message }) }
  }

  const columns: ColumnDef<AdminVideoTestimonial>[] = [
    { header: 'Author', accessorKey: 'author', cell: ({ getValue }) => <span className="font-medium text-sm">{String(getValue())}</span> },
    { header: 'Product', accessorKey: 'productName', cell: ({ getValue }) => <span className="text-sm text-slatey">{String(getValue() || '—')}</span> },
    { header: 'Duration', accessorKey: 'duration', cell: ({ getValue }) => <span className="text-xs tabular-nums">{String(getValue())}</span> },
    { header: 'Video', accessorKey: 'videoUrl', cell: ({ getValue }) => (<a href={String(getValue())} target="_blank" rel="noopener noreferrer" className="text-forest hover:underline text-xs inline-flex items-center gap-1"><Play className="size-3" /> View</a>) },
    { header: 'Date', accessorKey: 'date', cell: ({ getValue }) => <span className="text-xs text-slatey">{String(getValue())}</span> },
    { header: '', cell: ({ row }) => (<div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => openEdit(row.original)}><Pencil className="size-3.5" /></Button><Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(row.original.id)}><Trash2 className="size-3.5" /></Button></div>) },
  ]

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-ink">Video Testimonials</h1><p className="text-sm text-slatey mt-1">{data.length} video{data.length === 1 ? '' : 's'}</p></div>
        <Button onClick={openCreate}><Plus className="size-4" />Add Video</Button>
      </div>

      <DataTable data={data} columns={columns} emptyTitle="No video testimonials yet" emptyDescription="Add customer video testimonials for product pages." emptyAction={<Button onClick={openCreate}><Plus className="size-4" />Add</Button>} />

      {(creating || editing) && (
        <Dialog open onOpenChange={() => { setCreating(false); setEditing(null); }}>
          <DialogContent title={editing ? 'Edit Video' : 'New Video Testimonial'}>
            <div className="space-y-4">
              <FormField label="Product ID" required><Input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product UUID" /></FormField>
              <FormField label="Author" required><Input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Rajesh Kumar" /></FormField>
              <FormField label="Location"><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Delhi" /></FormField>
              <FormField label="Thumbnail URL" required><Input value={thumbnailUrl} onChange={(e) => setThumbnailUrl(e.target.value)} placeholder="https://..." /></FormField>
              <FormField label="Video URL" required><Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://...mp4" /></FormField>
              <FormField label="Duration" required><Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="1:24" /></FormField>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <DialogClose asChild><Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button></DialogClose>
              <Button onClick={handleSave} disabled={saving || !productId || !author || !thumbnailUrl || !videoUrl || !duration}>{saving ? 'Saving…' : editing ? 'Save' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
